import { PureComponent } from 'react'

import { noop } from 'lodash'

import { SortStyled } from './styles/SortStyled'
import { IconButton } from '@ui/elements/Button'
import {
  IconChevronUpDown,
  type IconElement,
  IconSortAscending,
  IconSortDescending,
} from '@ui/elements/Icon'
import { InputGroup } from '@ui/elements/InputGroup'
import { Select } from '@ui/elements/Select'

export interface SortKey {
  value: string
  label: string
}

export type SortDirection = 'asc' | 'desc'

export interface ISortProps {
  /** Value of the dropdown */
  value?: SortKey | string
  /** Array of Sort options to be in the dropdown */
  options?: SortKey[]
  /** Direction of the sort */
  direction?: SortDirection
  /** Set the initial value when uncontrolled */
  initialValue?: SortKey | string
  /** Set the initial direction when uncontrolled */
  initialDirection?: SortDirection
  onChange?: ({
    value,
    direction,
    nextDirection,
  }: {
    value: SortKey
    direction: SortDirection
    nextDirection: SortDirection
  }) => void
}

export interface ISortState {
  value?: SortKey
  direction?: SortDirection
}

const initialState: ISortState = {
  value: null,
  direction: null,
}

export const getSortKey = (
  value: ISortProps['value'],
  sortKeys: ISortProps['options']
): SortKey => {
  if (typeof value === 'string') {
    const key = value.replace(/-/, '')
    const sortKey = sortKeys.find(
      (sortKeyOption) => sortKeyOption.value === key
    )
    return sortKey ? { ...sortKey } : null
  } else return value
}

export class Sort extends PureComponent<ISortProps, ISortState> {
  static defaultProps = {
    onChange: noop,
  }

  readonly state = {
    ...initialState,
    value:
      getSortKey(this.props.initialValue, this.props.options) ||
      initialState.value,
    direction: this.props.initialDirection || initialState.direction,
  }

  isControlled = () => {
    return !!(this.props.value !== undefined)
  }

  onDirectionClick = () => {
    const controlled = this.isControlled()
    const direction = controlled ? this.props.direction : this.state.direction

    let nextDirection: SortDirection = null

    if (direction === 'asc') {
      nextDirection = 'desc'
    } else if (direction === 'desc') {
      nextDirection = 'asc'
    }

    if (controlled) {
      this.props.onChange({
        value: getSortKey(this.props.value, this.props.options),
        direction: this.props.direction,
        nextDirection,
      })

      return
    }

    this.setState(
      {
        direction: nextDirection,
      },
      () => {
        this.props.onChange({
          value: this.state.value,
          direction: this.state.direction,
          nextDirection: null,
        })
      }
    )
  }

  onSelectChange = (value) => {
    const controlled = this.isControlled()

    if (controlled) {
      this.props.onChange({
        value: getSortKey(value, this.props.options),
        direction: this.props.direction,
        nextDirection: null,
      })

      return
    }

    const direction = this.state.direction ? this.state.direction : 'desc'

    this.setState(
      {
        value,
        direction,
      },
      () => {
        this.props.onChange({
          value: this.state.value,
          direction,
          nextDirection: null,
        })
      }
    )
  }

  render() {
    const { options } = this.props
    const controlled = this.isControlled()

    const direction = controlled ? this.props.direction : this.state.direction
    const value = controlled
      ? getSortKey(this.props.value, this.props.options)
      : this.state.value

    let sortIcon: IconElement = null

    switch (direction) {
      case 'asc':
        sortIcon = <IconSortAscending aria-label='Sort ascending' />
        break
      case 'desc':
        sortIcon = <IconSortDescending aria-label='Sort descending' />
        break
      default:
        if (value) {
          sortIcon = <IconSortAscending aria-label='Sort ascending' />
        } else {
          sortIcon = <IconChevronUpDown aria-label='Sort descending' />
        }
        break
    }

    return (
      <SortStyled>
        <InputGroup>
          <InputGroup.Item>
            <IconButton
              iconComponent={sortIcon}
              onClick={this.onDirectionClick}
              disabled={!value}
              aria-label={
                direction === 'asc'
                  ? 'Sort in descending order'
                  : 'Sort in ascending order'
              }
              type='button'
            />
          </InputGroup.Item>
          <Select
            value={value}
            placeholder='Sort By'
            options={options}
            onChange={this.onSelectChange}
            isClearable={false}
          />
        </InputGroup>
      </SortStyled>
    )
  }
}
